import { createContext, useContext } from 'react';

// Chart-to-PNG export (SPEC/1466 phase 3). Each rendered ChartRenderer registers
// its Plotly graph div by chart id; a consumer (e.g. conciliatron's document
// generation) calls exportCharts() to snapshot every visible chart to a PNG data
// URL via Plotly.toImage. Charts render in the browser, so the PNGs are produced
// here and uploaded to storage-hub — the Go worker never renders Plotly.
//
// Without a provider the context is a no-op, so ChartRenderer stays usable
// standalone (the register calls just do nothing).
//
// The context object + hook live here (a .ts file) rather than alongside the
// ChartExportProvider component so the .tsx exports only the component — react
// fast-refresh (react-refresh/only-export-components) requires a component file
// to export nothing else. Mirrors dashboardDataContext.ts / DashboardDataProvider.tsx.

export interface ChartExportItem {
  /** The chart's config id, so the consumer can order PNGs by the dashboard. */
  id: string;
  /** A `data:image/png;base64,...` URL of the rendered chart. */
  dataUrl: string;
}

export interface ChartExportApi {
  /** Register (or replace) a chart's Plotly graph div. Null is ignored. */
  register: (id: string, graphDiv: HTMLElement | null) => void;
  /** Drop a chart from the registry (call on unmount). */
  unregister: (id: string) => void;
  /** Snapshot every registered chart to a PNG data URL, in registration order. */
  exportCharts: () => Promise<ChartExportItem[]>;
  /** How many charts are currently registered. */
  count: () => number;
}

const noop: ChartExportApi = {
  register: () => {},
  unregister: () => {},
  exportCharts: async () => [],
  count: () => 0,
};

export const ChartExportContext = createContext<ChartExportApi>(noop);

/** Access the chart export API. Returns a no-op API when no provider is mounted. */
export function useChartExport(): ChartExportApi {
  return useContext(ChartExportContext);
}
